'use client';
import { useState } from 'react';
import Link from 'next/link';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { ArrowRight } from '@/components/ui/icons';
import { useAuth } from '@/hooks/useAuth';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { SignUpModal } from '@/components/auth';
import { hasStoredSignupAccess } from '@/lib/signupAccessStorage';
/**
 * The Learn surface's account CTA. Learn is public, indexable documentation, so
 * most readers arrive signed out — and they hit the same access gate the
 * marketing nav enforces: no stored invite or promo code means the early-access
 * form, not a signup form. Signed-in readers get the way back into the app.
 */ export function LearnAccessCta() {
    const t = useTranslations$1();
    const { isAnonymous, isLoading } = useAuth();
    const hasSignupAccess = useStoredSignupAccess();
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [modalMode, setModalMode] = useState<'signup' | 'signin'>('signup');
    const pillClass = 'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md bg-white px-3 text-[13px] font-semibold text-black transition-colors hover:bg-white/90 whitespace-nowrap';
    // No CTA until auth resolves: rendering the signed-out pair first paints a
    // wrong "Request Access" flash for signed-in readers on a cold load, and a
    // click inside that window would open the waitlist form on a customer.
    if (isLoading) return null;
    if (!isAnonymous) {
        return (<Link href="/projects" className={pillClass}>
        {t("a1Olpu")}
        <ArrowRight className="h-3.5 w-3.5"/>
      </Link>);
    }
    return (<>
      { /* Login must exist here on its own: the access CTA routes no-code
          visitors to the waitlist form, and an existing customer on a new
          device is exactly a no-code visitor — the docs were their way in. */ }
      <button type="button" onClick={()=>{
        setModalMode('signin');
        setShowSignUpModal(true);
    }} className="rounded-md px-2.5 py-1.5 text-[13px] font-medium whitespace-nowrap text-muted-foreground transition-colors hover:text-foreground">
        {t("odXlk8")}
      </button>
      <button type="button" onClick={()=>{
        // Read storage at click time rather than trusting the render snapshot:
        // a code stored in another tab (or by the promo fetch) must not drop
        // an access holder into the waitlist form.
        if (hasStoredSignupAccess()) {
            setModalMode('signup');
            setShowSignUpModal(true);
        } else {
            window.open('https://form.typeform.com/to/VVwyeUrj', '_blank', 'noopener,noreferrer');
        }
    }} className={pillClass}>
        {hasSignupAccess ? t("8HJxXG") : t("UnR8YN")}
        <ArrowRight className="h-3.5 w-3.5"/>
      </button>
      <SignUpModal isOpen={showSignUpModal} onClose={()=>setShowSignUpModal(false)} returnPath="/projects" defaultMode={modalMode} variant="marketing"/>
    </>);
}
